/**
 * jolat.js — Jolat Proff kärn-API (Agent G). Vanilla ES2019, ingen build-step.
 * Laddas med defer i layout/theme.liquid. Kräver att theme.liquid först satt
 * window.JolatConfig och window.JolatCustomer (inline-script).
 *
 * Globalt API (SPEC §4):
 *   Jolat.taxRate, Jolat.taxesIncluded
 *   Jolat.getTaxMode() -> 'incl' | 'excl'   (localStorage 'jolat_tax_mode';
 *     default 'excl' för inloggad pro-kund, annars 'incl')
 *   Jolat.setTaxMode(mode)                  (sparar + dispatchar 'jolat:tax:changed')
 *   Jolat.displayOre(oreInkl)               (excl-läge: Math.round(ore / (1 + taxRate)))
 *   Jolat.money(ore)                        ("1 234 kr", sv-SE; öre visas bara om != 0)
 *   Jolat.formatWeight(gram)                ("4,2 kg")
 *   Jolat.updatePriceEl(el, ore[, opts])    ENDA tillåtna sättet att byta pris i DOM
 *     (opts: { compareOre, unitLabel }) — räknar själv pro-pris från data-pro-pct
 *   Jolat.refreshPrices([root])             re-rendera alla .jl-price i root
 *   Jolat.cart.get/add/change/update/note   wrappar /cart/*.js, dispatchar
 *     'jolat:cart:updated' (detail.cart); add öppnar drawern via 'jolat:cart:open'
 *   Jolat.openCart()                        dispatchar 'jolat:cart:open'
 *   Jolat.renderSections(ids[, url])        Section Rendering API (?sections=...)
 *   Jolat.shippingEstimate(gram)            billigaste DHL-rad ur fraktTabellen
 *
 * Events (document):
 *   'jolat:tax:changed'   detail { mode }
 *   'jolat:cart:updated'  detail = cart-objektet (lyssnare bör även tåla detail.cart)
 *   'jolat:cart:open'     (cart-drawer lyssnar och öppnar sig)
 *
 * DOM-kontrakt:
 *   .jl-price[data-ore][data-compare-ore][data-pro-pct][data-unit-label]
 *     med barn .jl-price__current/__compare/__tax/__unit/__saving
 *     samt [data-ore-saving] för "Du sparar"-beloppet — uppdateras vid momsväxling
 *     (bugg #3 i gamla temat: sparbeloppet uppdaterades inte).
 *   [data-jolat-tax-toggle][data-tax-mode="incl|excl"] — momsväxlarknappar,
 *     aria-pressed sköts här. Utan data-tax-mode: togglar nuvarande läge.
 *   [data-jolat-money][data-ore] — fristående belopp som ska följa momsläget.
 *   [data-jolat-tax-label][data-text-incl][data-text-excl] — textswap vid växling.
 *   [data-jolat-cart-count] / [data-jolat-cart-weight] — uppdateras vid cart-event.
 *   [data-jolat-shipping-estimate] — fraktestimat-text, uppdateras vid cart-event.
 *   <script type="application/json" data-jolat-shipping-table> — DHL-tabell
 *     (snippets/shipping-estimate.liquid): [{ "method": "DHL Service Point",
 *     "min_kg": 0, "max_kg": 2, "price_ore": 7900 }, ...]
 */
(function () {
  'use strict';

  var cfg = window.JolatConfig || {};
  var customer = window.JolatCustomer || { tier: 0, pct: 0, loggedIn: false };
  window.JolatCustomer = customer;

  var TAX_KEY = 'jolat_tax_mode';
  var strings = cfg.strings || {};
  var routes = cfg.routes || {};

  var fmtInt = new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 0 });
  var fmtDec = new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  var fmtKg = new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 1 });

  function readStorage(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
  function writeStorage(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) { /* privat läge */ }
  }
  function intAttr(el, name) {
    var v = parseInt(el.getAttribute(name), 10);
    return isNaN(v) ? 0 : v;
  }
  function fill(template, map) {
    var out = template || '';
    Object.keys(map).forEach(function (key) {
      out = out.split(key).join(map[key]);
    });
    return out;
  }

  var Jolat = {
    taxRate: typeof cfg.taxRate === 'number' ? cfg.taxRate : 0.25,
    taxesIncluded: cfg.taxesIncluded !== false,
    freeShippingThreshold: typeof cfg.freeShippingThreshold === 'number' ? cfg.freeShippingThreshold : 500000,

    getTaxMode: function () {
      var stored = readStorage(TAX_KEY);
      if (stored === 'incl' || stored === 'excl') return stored;
      return customer.tier > 0 ? 'excl' : 'incl';
    },

    setTaxMode: function (mode) {
      if (mode !== 'incl' && mode !== 'excl') return;
      writeStorage(TAX_KEY, mode);
      document.dispatchEvent(new CustomEvent('jolat:tax:changed', { detail: { mode: mode } }));
    },

    displayOre: function (oreIncl) {
      var ore = Math.round(oreIncl || 0);
      if (Jolat.taxesIncluded && Jolat.getTaxMode() === 'excl') {
        return Math.round(ore / (1 + Jolat.taxRate));
      }
      return ore;
    },

    money: function (ore) {
      ore = Math.round(ore || 0);
      var kr = ore / 100;
      return (ore % 100 === 0 ? fmtInt.format(kr) : fmtDec.format(kr)) + ' kr';
    },

    formatWeight: function (grams) {
      return fmtKg.format((grams || 0) / 1000) + ' kg';
    },

    /**
     * Uppdatera ett priselement med nytt öre-värde (inkl. moms, listpris).
     * ALDRIG skriva textContent direkt (bugg #2 i gamla temat: pro-priset
     * skrevs över) — pro-rabatten räknas om här från data-pro-pct.
     */
    updatePriceEl: function (el, ore, opts) {
      if (!el) return null;
      var target = null;
      if (el.classList && el.classList.contains('jl-price')) target = el;
      if (!target && el.querySelector) target = el.querySelector('.jl-price');
      if (!target && el.closest) target = el.closest('.jl-price');
      if (!target) return null;

      if (typeof ore === 'number' && isFinite(ore)) {
        target.setAttribute('data-ore', String(Math.round(ore)));
      }
      opts = opts || {};
      if (Object.prototype.hasOwnProperty.call(opts, 'compareOre')) {
        if (opts.compareOre && opts.compareOre > 0) {
          target.setAttribute('data-compare-ore', String(Math.round(opts.compareOre)));
        } else {
          target.removeAttribute('data-compare-ore');
        }
      }
      if (Object.prototype.hasOwnProperty.call(opts, 'unitLabel')) {
        var unitEl = target.querySelector('.jl-price__unit');
        if (opts.unitLabel) {
          target.setAttribute('data-unit-label', opts.unitLabel);
          if (unitEl) unitEl.textContent = opts.unitLabel;
        } else {
          target.removeAttribute('data-unit-label');
          if (unitEl) unitEl.textContent = '';
        }
      }
      renderPriceEl(target);
      return target;
    },

    refreshPrices: function (root) {
      var scope = root && root.querySelectorAll ? root : document;
      scope.querySelectorAll('.jl-price').forEach(renderPriceEl);
      scope.querySelectorAll('[data-jolat-money][data-ore]').forEach(renderMoneyEl);
      scope.querySelectorAll('[data-jolat-tax-label]').forEach(renderTaxLabelEl);
      syncTaxToggles();
    },

    openCart: function () {
      document.dispatchEvent(new CustomEvent('jolat:cart:open'));
    },

    /** Section Rendering API: Jolat.renderSections(['cart-drawer']) -> { 'cart-drawer': '<div ...' } */
    renderSections: function (ids, url) {
      var base = url || routes.root || '/';
      var sep = base.indexOf('?') > -1 ? '&' : '?';
      return window.fetch(base + sep + 'sections=' + ids.join(','), {
        headers: { Accept: 'application/json' }
      }).then(function (res) { return res.json(); });
    },

    shippingEstimate: function (grams) {
      var table = getShippingTable();
      if (!table || !table.length) return null;
      var kg = (grams || 0) / 1000;
      var best = null;
      table.forEach(function (row) {
        var min = typeof row.min_kg === 'number' ? row.min_kg : 0;
        var max = typeof row.max_kg === 'number' ? row.max_kg : Infinity;
        if (kg >= min && kg <= max) {
          if (!best || row.price_ore < best.price_ore) best = row;
        }
      });
      return best ? { method: best.method, priceOre: best.price_ore } : null;
    },

    cart: {
      get: function () {
        return window.fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then(parseJson);
      },
      add: function (items) {
        var list = Array.isArray(items) ? items : [items];
        return cartPost(routes.cartAdd || '/cart/add.js', { items: list })
          .then(function () { return Jolat.cart.get(); })
          .then(broadcastCart)
          .then(function (cart) {
            Jolat.openCart();
            return cart;
          });
      },
      change: function (line, quantity) {
        return cartPost(routes.cartChange || '/cart/change.js', { line: line, quantity: quantity })
          .then(broadcastCart);
      },
      update: function (payload) {
        return cartPost(routes.cartUpdate || '/cart/update.js', payload || {})
          .then(broadcastCart);
      },
      note: function (text) {
        return Jolat.cart.update({ note: text || '' });
      }
    }
  };

  /* ------------------------------------------------------------------ */
  /* Cart-hjälpare                                                       */
  /* ------------------------------------------------------------------ */
  function parseJson(res) {
    return res.json().then(function (data) {
      if (!res.ok) {
        var err = new Error((data && (data.description || data.message)) || 'Varukorgsfel');
        err.status = res.status;
        err.data = data;
        throw err;
      }
      return data;
    });
  }

  function cartPost(url, payload) {
    return window.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(parseJson);
  }

  function broadcastCart(cart) {
    document.dispatchEvent(new CustomEvent('jolat:cart:updated', { detail: cart }));
    return cart;
  }

  /* ------------------------------------------------------------------ */
  /* Prisrendering                                                       */
  /* ------------------------------------------------------------------ */
  function renderPriceEl(el) {
    var ore = parseInt(el.getAttribute('data-ore'), 10);
    if (isNaN(ore)) return;
    var pct = intAttr(el, 'data-pro-pct');
    var compare = intAttr(el, 'data-compare-ore');
    var mode = Jolat.getTaxMode();

    var effective = ore;
    var saving = 0;
    if (pct > 0) {
      effective = Math.round(ore * (100 - pct) / 100);
      saving = ore - effective;
    } else if (compare > ore) {
      saving = compare - ore;
    }

    var compareShow = 0;
    if (compare > ore) compareShow = compare;
    else if (pct > 0) compareShow = ore;

    var currentEl = el.querySelector('.jl-price__current');
    if (currentEl) currentEl.textContent = Jolat.money(Jolat.displayOre(effective));

    var compareEl = el.querySelector('.jl-price__compare');
    if (compareEl) {
      if (compareShow > effective) {
        compareEl.hidden = false;
        compareEl.textContent = Jolat.money(Jolat.displayOre(compareShow));
      } else {
        compareEl.hidden = true;
        compareEl.textContent = '';
      }
    }

    var savingEl = el.querySelector('[data-ore-saving]');
    if (savingEl) {
      savingEl.setAttribute('data-ore-saving', String(saving));
      savingEl.textContent = Jolat.money(Jolat.displayOre(saving));
      var savingWrap = el.querySelector('.jl-price__saving');
      if (savingWrap) savingWrap.hidden = saving <= 0;
    }

    var taxEl = el.querySelector('.jl-price__tax');
    if (taxEl) {
      taxEl.textContent = mode === 'excl' ? (strings.taxExcl || 'exkl. moms') : (strings.taxIncl || 'inkl. moms');
    }
  }

  function renderMoneyEl(el) {
    var ore = parseInt(el.getAttribute('data-ore'), 10);
    if (isNaN(ore)) return;
    el.textContent = Jolat.money(Jolat.displayOre(ore));
  }

  function renderTaxLabelEl(el) {
    var mode = Jolat.getTaxMode();
    var text = mode === 'excl' ? el.getAttribute('data-text-excl') : el.getAttribute('data-text-incl');
    if (text) el.textContent = text;
  }

  function syncTaxToggles() {
    var mode = Jolat.getTaxMode();
    document.querySelectorAll('[data-jolat-tax-toggle][data-tax-mode]').forEach(function (btn) {
      btn.setAttribute('aria-pressed', btn.getAttribute('data-tax-mode') === mode ? 'true' : 'false');
    });
  }

  /* ------------------------------------------------------------------ */
  /* Fraktestimat (DHL-tabell från snippets/shipping-estimate.liquid)    */
  /* ------------------------------------------------------------------ */
  var shippingTableCache = null;
  function getShippingTable() {
    if (shippingTableCache) return shippingTableCache;
    var el = document.querySelector('[data-jolat-shipping-table]');
    if (!el) return null;
    try {
      shippingTableCache = JSON.parse(el.textContent);
    } catch (e) {
      shippingTableCache = null;
    }
    return shippingTableCache;
  }

  function updateShippingEls(cart) {
    var els = document.querySelectorAll('[data-jolat-shipping-estimate]');
    if (!els.length) return;
    var est = Jolat.shippingEstimate(cart.total_weight);
    els.forEach(function (el) {
      if (!est || !cart.item_count) {
        el.textContent = '';
        return;
      }
      el.textContent = fill(strings.shippingFrom || '__PRICE__ (__METHOD__, __WEIGHT__)', {
        __PRICE__: Jolat.money(est.priceOre),
        __METHOD__: est.method,
        __WEIGHT__: Jolat.formatWeight(cart.total_weight)
      });
    });
  }

  function updateCartBadges(cart) {
    var empty = cart.item_count === 0;
    document.querySelectorAll('[data-jolat-cart-count]').forEach(function (el) {
      el.textContent = String(cart.item_count);
      el.classList.toggle('is-empty', empty);
      // Styr även hidden enligt markup-kontraktet (headern sätter hidden vid
      // tom varukorg) så badgen visas/döljs korrekt även om header-scriptet
      // inte hunnit/lyckats köra.
      el.hidden = empty;
    });
    document.querySelectorAll('[data-jolat-cart-weight]').forEach(function (el) {
      el.textContent = Jolat.formatWeight(cart.total_weight);
      el.hidden = empty;
    });
  }

  /* ------------------------------------------------------------------ */
  /* Init                                                                */
  /* ------------------------------------------------------------------ */
  function init() {
    // Sticky header-fallback för webbläsare utan :has() (bugg #4 i gamla temat)
    var header = document.querySelector('.jl-header');
    if (header) {
      var wrap = header.closest('.shopify-section');
      if (wrap) wrap.classList.add('jl-section-sticky');
    }

    // Momsväxlare: delegation så att även AJAX-renderade knappar funkar
    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      var btn = e.target.closest('[data-jolat-tax-toggle]');
      if (!btn) return;
      var mode = btn.getAttribute('data-tax-mode');
      if (mode !== 'incl' && mode !== 'excl') {
        mode = Jolat.getTaxMode() === 'incl' ? 'excl' : 'incl';
      }
      Jolat.setTaxMode(mode);
    });

    document.addEventListener('jolat:tax:changed', function () {
      Jolat.refreshPrices(document);
    });

    // Synk mellan flikar
    window.addEventListener('storage', function (e) {
      if (e.key === TAX_KEY && (e.newValue === 'incl' || e.newValue === 'excl')) {
        document.dispatchEvent(new CustomEvent('jolat:tax:changed', { detail: { mode: e.newValue } }));
      }
    });

    document.addEventListener('jolat:cart:updated', function (e) {
      var cart = e.detail || null;
      if (cart && cart.cart) cart = cart.cart;
      if (!cart || typeof cart.item_count !== 'number') return;
      updateCartBadges(cart);
      updateShippingEls(cart);
    });

    // Normalisera Liquid-renderade priser (momsläge + pro + sv-SE-format)
    Jolat.refreshPrices(document);
  }

  window.Jolat = Jolat;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
